import { questionSummaryKey, type ChoiceSummaryCounts } from './choiceSummary';
import { scaleOptionValue } from './questionScoring';
import {
  SEGMENT_BAND_IDS,
  type LocalizedText,
  type MetricFormat,
  type MetricResponse,
  type NpsBucket,
  type ReportMetricCalculation,
  type ReportMetricDefinition,
  type RespondentCondition,
  type ScaleBand,
  type ScaleSource,
  type ScoreSource,
} from './types';

type QuestionSummaries = ReadonlyMap<string, ChoiceSummaryCounts>;
type Responses = readonly MetricResponse[];

/** One input a metric is made of: a question (statement) or another metric. */
export type MetricPart = {
  id: string;
  value?: number;
  min: number;
  max: number;
  count: number;
  scale?: boolean;
} & ({ kind: 'question'; questionIndex: string; statementIndex?: number } | { kind: 'metric'; metricId: string });

export interface CustomMetricSummary {
  value?: number;
  min: number;
  max: number;
  count: number;
  status: 'available' | 'empty' | 'invalid';
  nps?: { detractors: number; passives: number; promoters: number; inputMin: 0 | 1 };
  /** True for scale metrics — their value is always shown with three decimal places. */
  scale?: boolean;
  /** Inputs of composed metrics (several questions or several metrics), for breakdown renderers. */
  parts?: MetricPart[];
  /** Respondent classification of `scaleBands` metrics, in definition order; percentages have three decimals. */
  bands?: MetricBand[];
  /** Thresholds of `respondentSegment` deviation conditions, in definition order. */
  thresholds?: SegmentThreshold[];
}

export interface MetricBand {
  id: string;
  label: LocalizedText;
  count: number;
  percentage: number;
  /** Colour hint when the band order does not run best to worst (a "matching" segment can be the bad one). */
  tone?: 'good' | 'bad';
}

/** Population statistics behind one `belowDeviation` condition, for previews; all three decimals. */
export interface SegmentThreshold {
  id: string;
  label: LocalizedText;
  mean?: number;
  deviation?: number;
  bound?: number;
  /** Respondents of the whole report with a score in this condition. */
  population: number;
}

/** Finds the first scale-total metric that directly or indirectly contains a question. */
export function findScaleMetricForQuestion(
  metrics: ReportMetricDefinition[],
  questionIndex: string,
): ReportMetricDefinition | undefined {
  const metricsById = new Map(metrics.map((metric) => [metric.id, metric]));
  const containsQuestion = (metric: ReportMetricDefinition, seen = new Set<string>()): boolean => {
    if (seen.has(metric.id) || metric.calculation.type !== 'scaleTotal') {
      return false;
    }
    const nextSeen = new Set(seen).add(metric.id);
    return (
      metric.calculation.sources.some((source) => source.questionIndex === questionIndex) ||
      (metric.calculation.metricIds ?? []).some((id) => {
        const child = metricsById.get(id);
        return child ? containsQuestion(child, nextSeen) : false;
      })
    );
  };
  return metrics.find((metric) => containsQuestion(metric));
}

export const roundToThree = (value: number): number => Math.round((value + Number.EPSILON) * 1000) / 1000;

/** Classifies a rounded per-respondent average: the first band (best to worst) whose lower bound it reaches. */
export function classifyScaleBand(value: number, bands: ScaleBand[]): string | undefined {
  return (bands.find((band) => value >= band.from) ?? bands[bands.length - 1])?.id;
}

/** Standard NPS buckets over a 0–10 (or 1–10) score. */
const npsBucketOf = (score: number): NpsBucket => (score <= 6 ? 'detractor' : score <= 8 ? 'passive' : 'promoter');

/**
 * Mean, sample standard deviation (n − 1) and mean − `deviations` × SD, each rounded to three decimals.
 * The deviation needs at least two scores; without it there is no bound and nobody can fall below it.
 */
export function deviationBound(scores: number[], deviations: number): Omit<SegmentThreshold, 'id' | 'label'> {
  const population = scores.length;
  if (!population) {
    return { population };
  }
  const mean = roundToThree(scores.reduce((total, score) => total + score, 0) / population);
  if (population < 2) {
    return { population, mean };
  }
  const variance = scores.reduce((total, score) => total + (score - mean) ** 2, 0) / (population - 1);
  const deviation = roundToThree(Math.sqrt(variance));
  return { population, mean, deviation, bound: roundToThree(mean - deviations * deviation) };
}

function sourceScore(source: ScoreSource, summaries: QuestionSummaries) {
  const summary = summaries.get(questionSummaryKey(source.questionIndex, source.statementIndex));
  let sum = 0;
  let count = 0;
  const nps = { detractors: 0, passives: 0, promoters: 0 };
  summary?.choices.forEach((choice) => {
    const score = scaleOptionValue(choice.index, source.optionScores);
    if (score == null || !Number.isFinite(score)) {
      return;
    }
    sum += score * choice.count;
    count += choice.count;
    if (score <= 6) {
      nps.detractors += choice.count;
    } else if (score <= 8) {
      nps.passives += choice.count;
    } else {
      nps.promoters += choice.count;
    }
  });
  return { value: count ? sum / count : undefined, min: source.min, max: source.max, count, nps };
}

function rawScaleScore(source: ScaleSource, summaries: QuestionSummaries): number | undefined {
  const summary = summaries.get(questionSummaryKey(source.questionIndex, source.statementIndex));
  let sum = 0;
  let count = 0;
  summary?.choices.forEach((choice) => {
    const score = scaleOptionValue(choice.index, source.optionScores ?? []);
    if (score == null || !Number.isFinite(score)) {
      return;
    }
    sum += score * choice.count;
    count += choice.count;
  });
  return count ? sum / count : undefined;
}

type Response = MetricResponse;

/**
 * Report-wide deviation bounds depend only on the population, the merged sources and the option counts, never on
 * the cohort, so every team and participant group shares one calculation instead of re-scanning every response.
 */
const populationThresholds = new WeakMap<Responses, Map<string, Omit<SegmentThreshold, 'id' | 'label'>>>();

/**
 * Runs once per cohort from merged sufficient statistics, never from rendered cells/child averages.
 * `population` holds every response of the report; report-wide thresholds (deviation bounds) are taken from it
 * so that a respondent's classification does not depend on the cohort being viewed.
 */
export function calculateCustomMetrics(
  metrics: ReportMetricCalculation[],
  summaries: QuestionSummaries,
  responses: Responses = [],
  population: Responses = responses,
) {
  const results: Record<string, CustomMetricSummary> = Object.create(null);
  const metricsById = new Map(metrics.map((metric) => [metric.id, metric]));
  const visiting = new Set<string>();
  const optionCount = (source: ScaleSource) =>
    summaries.get(questionSummaryKey(source.questionIndex, source.statementIndex))?.choices.length ?? 0;
  const questionPart = (source: ScaleSource, value: number | undefined, min: number, max: number, count: number) =>
    ({
      kind: 'question',
      id: questionSummaryKey(source.questionIndex, source.statementIndex),
      questionIndex: source.questionIndex,
      statementIndex: source.statementIndex,
      value,
      min,
      max,
      count,
    }) satisfies MetricPart;
  const metricPart = (metricId: string, summary: CustomMetricSummary): MetricPart => ({
    kind: 'metric',
    id: metricId,
    metricId,
    value: summary.value,
    min: summary.min,
    max: summary.max,
    count: summary.count,
    scale: summary.scale,
  });
  /**
   * Own scale sources plus those of referenced `scaleTotal` metrics, deduplicated. Cycles are cut silently.
   * The root may also be a `scaleBands` metric; referenced metrics are always `scaleTotal`.
   */
  const effectiveScaleSources = (metricId: string, seen = new Set<string>()): ScaleSource[] => {
    const calculation = metricsById.get(metricId)?.calculation;
    if (!calculation || seen.has(metricId)) {
      return [];
    }
    if (calculation.type !== 'scaleTotal' && calculation.type !== 'scaleBands') {
      return [];
    }
    if (calculation.type === 'scaleBands' && seen.size > 0) {
      return [];
    }
    seen.add(metricId);
    return mergeScaleSources(calculation.sources, calculation.metricIds, seen);
  };
  /** `sources` plus the questions of referenced `scaleTotal` metrics, deduplicated by question/statement. */
  const mergeScaleSources = (
    sources: ScaleSource[],
    metricIds: string[] | undefined,
    seen = new Set<string>(),
  ): ScaleSource[] => {
    const merged = [...sources, ...(metricIds ?? []).flatMap((id) => effectiveScaleSources(id, seen))];
    const keys = new Set<string>();
    return merged.filter((source) => {
      const key = questionSummaryKey(source.questionIndex, source.statementIndex);
      if (keys.has(key)) {
        return false;
      }
      keys.add(key);
      return true;
    });
  };
  /** Valid scale values of one respondent's questionnaire over the given sources; unanswered questions are skipped. */
  const respondentScaleValues = (response: Response, sources: ScaleSource[]): number[] => {
    const values: number[] = [];
    for (const source of sources) {
      const options = optionCount(source);
      const answers =
        response.messages?.filter(
          (item) =>
            item.questionDefinitionIndex === source.questionIndex &&
            (source.statementIndex === undefined || item.statementIndex === source.statementIndex),
        ) ?? [];
      for (const answer of answers) {
        const raw = answer.value?.trim() ?? '';
        if (raw === '' || /^-\d+$/.test(raw)) {
          continue;
        }
        const tokens = raw.split(',').map((value) => value.trim());
        const indexes = tokens.map(Number);
        const valid = indexes.length === 1 && tokens.every((token) => /^\d+$/.test(token)) && indexes[0] < options;
        if (!valid) {
          continue;
        }
        const score = scaleOptionValue(indexes[0], source.optionScores ?? []);
        if (score != null && Number.isFinite(score)) {
          values.push(score);
        }
      }
    }
    return values;
  };
  const calculateScaleTotal = (sources: ScaleSource[]) => {
    let sum = 0;
    let count = 0;
    for (const response of responses) {
      for (const value of respondentScaleValues(response, sources)) {
        sum += value;
        count++;
      }
    }
    return {
      value: count ? roundToThree(sum / count) : undefined,
      count,
    };
  };
  /**
   * Each respondent's own average (sum of their answers / number of their answered questions, three decimals),
   * never an average of category averages. Respondents without a valid answer are left out.
   */
  const respondentScaleAverage = (response: Response, sources: ScaleSource[]): number | undefined => {
    const values = respondentScaleValues(response, sources);
    return values.length ? roundToThree(values.reduce((total, value) => total + value, 0) / values.length) : undefined;
  };
  const respondentScaleAverages = (sources: ScaleSource[], from: Responses = responses): number[] =>
    from.flatMap((response) => {
      const average = respondentScaleAverage(response, sources);
      return average === undefined ? [] : [average];
    });
  /** The respondent's single valid NPS answer mapped through the source's option scores, as a bucket. */
  const respondentNpsBucket = (response: Response, source: ScoreSource): NpsBucket | undefined => {
    const answers =
      response.messages?.filter(
        (item) =>
          item.questionDefinitionIndex === source.questionIndex &&
          (source.statementIndex === undefined || item.statementIndex === source.statementIndex),
      ) ?? [];
    for (const answer of answers) {
      const raw = answer.value?.trim() ?? '';
      if (!/^\d+$/.test(raw)) {
        continue;
      }
      const score = source.optionScores[Number(raw)];
      if (score != null && Number.isFinite(score)) {
        return npsBucketOf(score);
      }
    }
    return undefined;
  };
  /**
   * Evaluates `condition` for one respondent: true/false, or undefined when the respondent gave no usable answer
   * (such respondents are excluded from the segment's denominator).
   */
  const evaluateCondition = (
    response: Response,
    condition: RespondentCondition,
    /** Merged scale sources of a `belowDeviation` condition, resolved once per metric rather than per respondent. */
    sources: ScaleSource[],
    thresholds: Map<string, SegmentThreshold>,
  ): boolean | undefined => {
    if (condition.kind === 'npsBucket') {
      const bucket = respondentNpsBucket(response, condition.source);
      return bucket === undefined ? undefined : condition.buckets.includes(bucket);
    }
    const score = respondentScaleAverage(response, sources);
    const bound = thresholds.get(condition.id)?.bound;
    if (score === undefined) {
      return undefined;
    }
    // "Lower bound negative": the respondent's score minus the bound is below zero.
    return bound === undefined ? false : roundToThree(score - bound) < 0;
  };
  const evaluate = (metric: ReportMetricCalculation): CustomMetricSummary => {
    if (results[metric.id]) {
      return results[metric.id];
    }
    const calculation = metric.calculation;
    const result: CustomMetricSummary = {
      status: 'empty',
      min: 0,
      max: 100,
      count: 0,
    };
    if (visiting.has(metric.id)) {
      return { ...result, status: 'invalid' };
    }
    visiting.add(metric.id);
    if (calculation.type === 'nps') {
      const source = sourceScore(calculation.source, summaries);
      result.min = -100;
      result.count = source.count;
      result.nps = { ...source.nps, inputMin: calculation.inputMin };
      if (source.count) {
        result.value = (100 * (source.nps.promoters - source.nps.detractors)) / source.count;
      }
    } else if (calculation.type === 'choiceMatchRate') {
      const eligible = new Set<string>();
      const matching = new Set<string>();
      calculation.sources.forEach((source) => {
        const summary = summaries.get(questionSummaryKey(source.questionIndex, source.statementIndex));
        summary?.respondingChannelIds.forEach((key) => eligible.add(key));
        source.optionIndexes.forEach((index) => summary?.choiceResponseIds[index]?.forEach((key) => matching.add(key)));
      });
      result.count = eligible.size;
      if (result.count) {
        result.value = (100 * (calculation.complement ? result.count - matching.size : matching.size)) / result.count;
      }
    } else if (calculation.type === 'scaleTotal') {
      const sources = effectiveScaleSources(metric.id);
      const totalScore = calculateScaleTotal(sources);
      result.value = totalScore.value;
      result.count = totalScore.count;
      result.min = 1;
      result.max = Math.max(
        1,
        ...sources.map((source) =>
          Math.max(1, ...(source.optionScores ?? []).filter((score): score is number => score != null)),
        ),
      );
      result.scale = true;
      // A metric composed of other metrics breaks down into those; a plain one into its questions.
      const parts: MetricPart[] = [
        ...(calculation.metricIds ?? []).flatMap((id) => {
          const child = metricsById.get(id);
          return child ? [metricPart(id, evaluate(child))] : [];
        }),
        ...calculation.sources.map((source) => {
          const summary = summaries.get(questionSummaryKey(source.questionIndex, source.statementIndex));
          const count = summary?.choices.reduce((total, choice) => total + choice.count, 0) ?? 0;
          return {
            ...questionPart(source, rawScaleScore(source, summaries), 1, optionCount(source), count),
            scale: true,
          };
        }),
      ];
      if (parts.length) {
        result.parts = parts;
      }
    } else if (calculation.type === 'scaleBands') {
      const averages = respondentScaleAverages(effectiveScaleSources(metric.id));
      const counts = new Map(calculation.bands.map((band) => [band.id, 0]));
      averages.forEach((average) => {
        const bandId = classifyScaleBand(average, calculation.bands);
        if (bandId !== undefined) {
          counts.set(bandId, (counts.get(bandId) ?? 0) + 1);
        }
      });
      result.count = averages.length;
      result.bands = calculation.bands.map((band) => {
        const count = counts.get(band.id) ?? 0;
        return {
          id: band.id,
          label: band.label,
          count,
          percentage: averages.length ? roundToThree((100 * count) / averages.length) : 0,
        };
      });
      if (averages.length) {
        result.value = result.bands.find((band) => band.id === calculation.primaryBandId)?.percentage;
      }
    } else if (calculation.type === 'respondentSegment') {
      const thresholds = new Map<string, SegmentThreshold>();
      // Sources are merged once per condition; the per-respondent loop below only reads them.
      const conditionSources = calculation.conditions.map((condition) =>
        condition.kind === 'belowDeviation' ? mergeScaleSources(condition.sources, condition.metricIds) : [],
      );
      calculation.conditions.forEach((condition, index) => {
        if (condition.kind !== 'belowDeviation') {
          return;
        }
        const sources = conditionSources[index];
        const cache = populationThresholds.get(population) ?? new Map();
        populationThresholds.set(population, cache);
        const key = JSON.stringify([sources, condition.deviations, sources.map(optionCount)]);
        const bound =
          cache.get(key) ?? deviationBound(respondentScaleAverages(sources, population), condition.deviations);
        cache.set(key, bound);
        thresholds.set(condition.id, { id: condition.id, label: condition.label, ...bound });
      });
      let evaluable = 0;
      let matching = 0;
      responses.forEach((response) => {
        const verdicts = calculation.conditions.map((condition, index) =>
          evaluateCondition(response, condition, conditionSources[index], thresholds),
        );
        if (verdicts.some((verdict) => verdict === undefined)) {
          return;
        }
        evaluable++;
        if (verdicts.every(Boolean)) {
          matching++;
        }
      });
      const share = (count: number) => (evaluable ? roundToThree((100 * count) / evaluable) : 0);
      result.count = evaluable;
      result.thresholds = [...thresholds.values()];
      result.bands = [
        {
          id: SEGMENT_BAND_IDS.matching,
          label: calculation.labels.matching,
          count: matching,
          percentage: share(matching),
          tone: 'bad',
        },
        {
          id: SEGMENT_BAND_IDS.other,
          label: calculation.labels.other,
          count: evaluable - matching,
          percentage: share(evaluable - matching),
          tone: 'good',
        },
      ];
      if (evaluable) {
        result.value = share(matching);
      }
    } else {
      const parts: MetricPart[] =
        calculation.type === 'average' || calculation.type === 'esi'
          ? calculation.sources.map((source) => {
              const score = sourceScore(source, summaries);
              const scoreValue =
                calculation.type === 'esi' ? (score.value !== undefined ? score.value * 10 : undefined) : score.value;
              const min = calculation.type === 'esi' ? score.min * 10 : score.min;
              const max = calculation.type === 'esi' ? score.max * 10 : score.max;
              return questionPart(source, scoreValue, min, max, score.count);
            })
          : calculation.metricIds.flatMap((id) => {
              const source = metricsById.get(id);
              return source ? [metricPart(id, evaluate(source))] : [];
            });
      const sources: { value?: number; min: number; max: number; count: number }[] = parts;
      if (parts.length) {
        result.parts = parts;
      }
      if (calculation.type === 'compositeAverage' && parts.length && parts.every((part) => part.scale)) {
        result.scale = calculation.normalization === 'none';
      }
      const available = sources.filter((source) => source.value !== undefined);
      result.count = available.reduce((total, source) => total + source.count, 0);
      if (calculation.normalization === 'none' && sources.length) {
        result.min = sources[0].min;
        result.max = sources[0].max;
      }
      if (available.length) {
        result.value =
          available.reduce(
            (total, source) =>
              total +
              (calculation.normalization === 'range'
                ? (100 * (source.value! - source.min)) / (source.max - source.min)
                : source.value!),
            0,
          ) / available.length;
      }
    }
    if (result.value !== undefined) {
      result.status = Number.isFinite(result.value) ? 'available' : 'invalid';
    }
    if (result.status === 'invalid') {
      result.value = undefined;
    }
    results[metric.id] = result;
    visiting.delete(metric.id);
    return result;
  };
  metrics.forEach(evaluate);
  return results;
}

/** Display formatting of a calculated value; the format comes from the metric definition, never from the summary. */
export function metricDisplay(summary: CustomMetricSummary, format: MetricFormat) {
  const { value, min, max } = summary;
  if (value === undefined) {
    return { value: undefined, min, max, suffix: '' };
  }
  if (format === 'rangePercent') {
    return { value: (100 * (value - min)) / (max - min), min: 0, max: 100, suffix: '%' };
  }
  if (format === 'maxPercent') {
    return { value: (100 * value) / max, min: 0, max: 100, suffix: '%' };
  }
  return { value, min, max, suffix: format === 'outOfMax' ? `/${max}` : '' };
}
